import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { PassageProgressSnapshot } from '../api/learnerPassageProgressApi'

type ProgressRecord = {
  progress: PassageProgressSnapshot
  completedAt: number | null
  rewardPointsAwarded: number | null
  hydrated: boolean
  dirty: boolean
  revision: number
}

interface LearnerPassageProgressState {
  byPassageId: Record<string, ProgressRecord | undefined>
}

const emptyProgress = (): PassageProgressSnapshot => ({ activities: {} })
const initialState: LearnerPassageProgressState = { byPassageId: {} }

const learnerPassageProgressSlice = createSlice({
  name: 'learnerPassageProgress',
  initialState,
  reducers: {
    hydrateProgress: (state, action: PayloadAction<{
      passageId: string
      progress: PassageProgressSnapshot
      completedAt: number | null
      rewardPointsAwarded: number | null
    }>) => {
      state.byPassageId[action.payload.passageId] = {
        progress: action.payload.progress ?? emptyProgress(),
        completedAt: action.payload.completedAt ?? null,
        rewardPointsAwarded: action.payload.rewardPointsAwarded ?? null,
        hydrated: true,
        dirty: false,
        revision: 0,
      }
    },
    setActivityProgress: (state, action: PayloadAction<{ passageId: string; activityCode: string; value: Record<string, unknown> }>) => {
      const record = state.byPassageId[action.payload.passageId] ?? { progress: emptyProgress(), completedAt: null, rewardPointsAwarded: null, hydrated: true, dirty: false, revision: 0 }
      record.progress.current_activity = action.payload.activityCode
      const nextActivity = {
        ...(record.progress.activities?.[action.payload.activityCode] ?? {}),
      }
      Object.entries(action.payload.value).forEach(([key, value]) => {
        if (value === undefined) delete nextActivity[key]
        else nextActivity[key] = value
      })
      record.progress.activities = {
        ...(record.progress.activities ?? {}),
        [action.payload.activityCode]: nextActivity,
      }
      record.dirty = true
      record.revision += 1
      state.byPassageId[action.payload.passageId] = record
    },
    markProgressSaved: (state, action: PayloadAction<{ passageId: string; revision: number }>) => {
      const record = state.byPassageId[action.payload.passageId]
      if (record && record.revision === action.payload.revision) record.dirty = false
    },
    setCurrentPassageActivity: (state, action: PayloadAction<{ passageId: string; activityCode: string }>) => {
      const record = state.byPassageId[action.payload.passageId]
      if (!record || record.progress.current_activity === action.payload.activityCode) return
      record.progress.current_activity = action.payload.activityCode
      record.dirty = true
      record.revision += 1
    },
    markPassageCompleted: (state, action: PayloadAction<{ passageId: string; completedAt: number; rewardPointsAwarded: number }>) => {
      const record = state.byPassageId[action.payload.passageId]
      if (!record) return
      record.completedAt = action.payload.completedAt
      record.rewardPointsAwarded = action.payload.rewardPointsAwarded
      record.dirty = false
    },
  },
})

export const { hydrateProgress, markPassageCompleted, markProgressSaved, setActivityProgress, setCurrentPassageActivity } = learnerPassageProgressSlice.actions
export default learnerPassageProgressSlice.reducer
